import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/cn";

type Service = {
  id: string;
  name: string;
  duration_minutes: number;
  price_cents: number;
  photo_asset_id?: string | null;
};

type CatalogAsset = {
  url: string;
  variants?: Record<string, string>;
  alt_text?: string | null;
};

type ServiceCardStyle = {
  variant?: "list" | "grid" | "cardsWithImages";
  showDurationBadge?: boolean;
  showPrice?: boolean;
  imageMode?: "cover" | "contain";
};

type Props = {
  services: Service[];
  assets: Record<string, CatalogAsset>;
  styleConfig?: ServiceCardStyle;
  selectedId: string;
  onSelect: (id: string) => void;
  onContinue: () => void;
};

export function StepServices({ services, assets, styleConfig, selectedId, onSelect, onContinue }: Props) {
  const { t } = useTranslation();
  const tr = (key: string, fallback: string, options?: Record<string, unknown>) => {
    const translated = t(key, { ...options, defaultValue: fallback });
    return translated === key ? fallback : translated;
  };
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const variant = styleConfig?.variant || "grid";
  const showDurationBadge = styleConfig?.showDurationBadge ?? true;
  const showPrice = styleConfig?.showPrice ?? true;
  const imageMode = styleConfig?.imageMode || "cover";

  const handleSelect = (id: string) => {
    onSelect(id);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(onContinue, 400);
  };

  const gridClass =
    variant === "list"
      ? "grid grid-cols-1 gap-3"
      : "grid grid-cols-1 sm:grid-cols-2 gap-3";

  return (
    <div className="step-enter">
      <h2 className="text-lg font-semibold text-slate-800 mb-4">
        {tr("client.booking.step.services.title", "Choose a service")}
      </h2>

      <div className={gridClass}>
        {services.map((svc) => {
          const selected = selectedId === svc.id;
          const asset = svc.photo_asset_id ? assets[svc.photo_asset_id] : undefined;
          const showImage = variant === "cardsWithImages";
          return (
            <button
              key={svc.id}
              onClick={() => handleSelect(svc.id)}
              data-testid={`service-card-${svc.id}`}
              className={cn(
                "relative text-left rounded-2xl border p-4 transition-all duration-200",
                "hover:shadow-md hover:-translate-y-0.5",
                selected
                  ? "border-teal-600 bg-teal-50"
                  : "border-slate-200 bg-white"
              )}
            >
              {selected && (
                <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-teal-600 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
              )}
              {showImage && (
                <div className="mb-3 h-28 rounded-xl border border-slate-200 bg-slate-100 overflow-hidden">
                  {asset?.url ? (
                    <img
                      src={asset.variants?.card_4x3 || asset.variants?.card_1x1 || asset.variants?.web_1600 || asset.url}
                      alt={asset.alt_text || svc.name}
                      className={`h-full w-full ${imageMode === "contain" ? "object-contain bg-white" : "object-cover"}`}
                    />
                  ) : (
                    <div className="h-full w-full bg-slate-100" />
                  )}
                </div>
              )}
              <p className="font-semibold text-slate-800">{svc.name}</p>
              <div className="flex items-center gap-2 mt-2">
                {showDurationBadge && (
                  <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 6v6l4 2" />
                    </svg>
                    {tr("client.booking.durationMinutes", "{{count}} min", { count: svc.duration_minutes })}
                  </span>
                )}
                {showPrice && (
                  <span className="text-sm font-bold" style={{ color: "var(--client-primary)" }}>
                    ${(svc.price_cents / 100).toFixed(2)}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {selectedId && (
        <button
          onClick={onContinue}
          className="btn mt-4 w-full sm:w-auto"
        >
          {tr("common.continue", "Continue")}
        </button>
      )}
    </div>
  );
}
